// @ts-nocheck
import React from 'react';
import { format } from 'date-fns';
import type { AdminUser } from '../../types/admin';
import { useSwipeGesture } from './useSwipeGesture';

interface AdminUserCardProps {
  user: AdminUser;
  isSelected: boolean;
  updatingRole: string | null;
  onSelect: (userId: string, selected: boolean) => void;
  onRoleChange: (userId: string, newRole: 'user' | 'admin' | 'super_admin') => void;
  onStatusToggle: (userId: string, currentStatus: boolean | undefined) => void;
  onViewDetails: (user: AdminUser) => void;
}

export const AdminUserCard: React.FC<AdminUserCardProps> = ({
  user,
  isSelected,
  updatingRole,
  onSelect,
  onRoleChange,
  onStatusToggle,
  onViewDetails,
}) => {
  const swipeGesture = useSwipeGesture({
    onSwipeLeft: () => {
      if (!isSelected) {
        onSelect(user.id, true);
      }
    },
    onSwipeRight: () => {
      if (isSelected) {
        onSelect(user.id, false);
      }
    },
    threshold: 50,
  });

  return (
    <div
      className="bg-white dark:bg-luna-primary rounded-xl border-2 border-luna-secondary/30 p-4 hover:shadow-lg transition-shadow relative overflow-hidden"
      {...swipeGesture}
    >
      <div className="flex items-start justify-between mb-3">
        <button
          onClick={() => onViewDetails(user)}
          className="text-left flex-1"
        >
          <h3 className="font-semibold text-luna-text-light dark:text-luna-text-dark">
            {user.first_name || user.last_name
              ? `${user.first_name || ''} ${user.last_name || ''}`.trim()
              : 'No name'}
          </h3>
          {user.email && (
            <p className="text-sm text-luna-text-light/70 dark:text-luna-text-dark/70 mt-1">
              {user.email}
            </p>
          )}
        </button>
        <input
          type="checkbox"
          checked={isSelected}
          onChange={(e) => onSelect(user.id, e.target.checked)}
          className="rounded border-luna-secondary/30"
          aria-label={`Select user ${user.first_name || user.id}`}
        />
      </div>
      <div className="space-y-2 text-sm">
        <div className="flex items-center justify-between">
          <span className="text-luna-text-light/70 dark:text-luna-text-dark/70">Role:</span>
          <select
            value={user.role}
            onChange={(e) => onRoleChange(user.id, e.target.value as 'user' | 'admin' | 'super_admin')}
            disabled={updatingRole === user.id}
            className="px-2 py-1 rounded border border-luna-secondary/30 bg-white dark:bg-luna-primary text-luna-text-light dark:text-luna-text-dark text-xs min-h-[32px] touch-manipulation"
          >
            <option value="user">User</option>
            <option value="admin">Admin</option>
            <option value="super_admin">Super Admin</option>
          </select>
        </div>
        <div className="flex items-center justify-between">
          <span className="text-luna-text-light/70 dark:text-luna-text-dark/70">Status:</span>
          <button
            onClick={() => onStatusToggle(user.id, user.is_active)}
            className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-colors min-h-[32px] touch-manipulation ${
              user.is_active !== false
                ? 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400'
                : 'bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-400'
            }`}
          >
            {user.is_active !== false ? 'Active' : 'Inactive'}
          </button>
        </div>
        <div className="text-xs text-luna-text-light/70 dark:text-luna-text-dark/70 pt-2 border-t border-luna-secondary/10">
          Created: {format(new Date(user.created_at), 'MMM dd, yyyy')}
        </div>
      </div>
      {/* Swipe indicator */}
      <div className="absolute inset-y-0 left-0 w-2 bg-luna-secondary/20 opacity-0 transition-opacity pointer-events-none" />
    </div>
  );
};

